import type { InfiniteData } from "react-query";
import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import { useVenueBasedMembershipGroupService } from "@app-contexts/dashboard/features/memberships/data/services/membership-group/useVenueBasedMembershipGroupService";
import type { ComboBoxInputItemType } from "@ui-kit/form/ComboBox/ComboBox.props";
import type { SearchServiceConfig } from "@shared/components/application/form/FormField/types";
import type { PaginatorContract } from "@shared/services/api/types";
import { getFlattenedData } from "@shared/utils/data/data-utils";

export default function useSearchMembershipGroup({
  membershipGroupSearchInput,
  enabled = true,
  customParams,
  single = false,
  service: serviceOverride,
}: {
  membershipGroupSearchInput: string;
  enabled?: boolean;
  customParams?: Record<string, unknown>;
  single?: boolean;
  service?: SearchServiceConfig;
}): {
  items: VenueBasedMembershipGroupSerializer[] | ComboBoxInputItemType[];
  isFetching: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
} {
  const defaultService = useVenueBasedMembershipGroupService();
  const { usePaginated } = serviceOverride ?? defaultService;

  const {
    data: membershipGroupSearchData,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePaginated({
    params: {
      q: membershipGroupSearchInput,
      page_size: 5,
      ...customParams,
    },
    options: { enabled },
  });

  const flattenedData = membershipGroupSearchData
    ? getFlattenedData(
        membershipGroupSearchData as InfiniteData<
          PaginatorContract<VenueBasedMembershipGroupSerializer>
        >
      )
    : [];

  const singleSelectItems: VenueBasedMembershipGroupSerializer[] =
    flattenedData as VenueBasedMembershipGroupSerializer[];

  const multiSelectItems: ComboBoxInputItemType[] =
    membershipGroupSearchData && membershipGroupSearchData.pages
      ? (flattenedData as VenueBasedMembershipGroupSerializer[]).map(
          (item) => ({
            id: item.id,
            name: item.name,
            displayName: item.name,
            description: item.description,
          })
        )
      : ([] as ComboBoxInputItemType[]);

  return {
    items: single ? singleSelectItems : multiSelectItems,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}
